import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import DatasetCard, { DatasetCardItem } from '../DatasetCard/DatasetCard';
import './DatasetGrid.css';

const SEARCH_DEBOUNCE_MS = 300;

const PARAM_SORT = 'sort';
const PARAM_TYP = 'typ';

/** Normalizace pro vyhledávání: lowercase, bez diakritiky, bez interpunkce (Zlín = zlin) */
function normalizeForSearch(s: string): string {
    const lower = (s ?? '').toLowerCase();
    const withoutAccents = lower.normalize('NFD').replace(/\p{M}/gu, '');
    return withoutAccents.replace(/[\p{P}\p{S}]/gu, '');
}

/** Mapuje typ z databáze na klíč. */
function getTypKey(typNazev: string): string {
    const lower = typNazev.toLowerCase();
    if (lower.includes('kraj')) return 'regions';
    if (lower.includes('město') || lower.includes('mesto')) return 'cities';
    if (lower.includes('parlament')) return 'national';
    return 'cities';
}

type Props = {
    datasets: DatasetCardItem[];
};

function computeFiltered(
    datasetsWithNorm: { item: DatasetCardItem; titleNorm: string; typNorm: string; dateNorm: string }[],
    typFilter: string,
    searchFilter: string,
    sort: string,
    collator: Intl.Collator
): DatasetCardItem[] {
    let list = datasetsWithNorm;

    if (typFilter) {
        list = list.filter((x) => getTypKey(x.item.typCode ?? '') === typFilter);
    }

    if (searchFilter.trim()) {
        const q = normalizeForSearch(searchFilter);
        list = list.filter((x) => x.titleNorm.includes(q) || x.typNorm.includes(q) || x.dateNorm.includes(q));
    }

    const items = list.map((x) => x.item);

    switch (sort) {
        case 'title-desc':
            items.sort((a, b) => collator.compare(b.title ?? '', a.title ?? ''));
            break;
        case 'date-desc':
            items.sort((a, b) =>
                (b.posledniAktualizace ?? '').localeCompare(a.posledniAktualizace ?? '')
            );
            break;
        case 'date-asc':
            items.sort((a, b) =>
                (a.posledniAktualizace ?? '').localeCompare(b.posledniAktualizace ?? '')
            );
            break;
        default:
            items.sort((a, b) => collator.compare(a.title ?? '', b.title ?? ''));
    }

    return items;
}

const DatasetGrid = ({ datasets }: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [filteredResult, setFilteredResult] = useState<DatasetCardItem[]>([]);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { t, i18n } = useTranslation('datasets');

    const sort = searchParams.get(PARAM_SORT) ?? 'title-asc';
    const typFilter = searchParams.get(PARAM_TYP) ?? '';

    const collator = useMemo(
        () =>
            new Intl.Collator(i18n.language, {
                sensitivity: 'base',
                numeric: true
            }),
        [i18n.language]
    );

    const SORT_OPTIONS = useMemo(() => [
        { label: t('sortOption.titleNameAZ'), value: 'title-asc' },
        { label: t('sortOption.titleNameZA'), value: 'title-desc' },
        { label: t('sortOption.dateNewest'), value: 'date-desc' },
        { label: t('sortOption.dateOldest'), value: 'date-asc' }
    ], [t]);

    const datasetsWithNorm = useMemo(
        () =>
            datasets.map((d) => ({
                item: d,
                titleNorm: normalizeForSearch(d.title),
                typNorm: normalizeForSearch(d.typCode ?? ''),
                dateNorm: normalizeForSearch(d.posledniAktualizace ?? ''),
            })),
        [datasets]
    );

    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            const result = computeFiltered(datasetsWithNorm, typFilter, searchFilter, sort, collator);
            setFilteredResult(result);
            setIsPending(false);
        });
        return () => cancelAnimationFrame(raf);
    }, [datasetsWithNorm, typFilter, searchFilter, sort, collator]);

    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            searchDebounceRef.current = null;
            setSearchFilter(searchInput);
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, [searchInput]);

    const updateParams = useCallback(
        (next: URLSearchParams) => {
            const scrollY = window.scrollY;
            setSearchParams(next, { replace: true });
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    window.scrollTo(0, scrollY);
                });
            });
        },
        [setSearchParams]
    );

    const setSort = useCallback(
        (value: string) => {
            setIsPending(true);
            const next = new URLSearchParams(searchParams);
            next.set(PARAM_SORT, value);
            updateParams(next);
        },
        [searchParams, updateParams]
    );

    const setTypFilter = useCallback(
        (value: string) => {
            setIsPending(true);
            const next = new URLSearchParams(searchParams);
            if (value) next.set(PARAM_TYP, value);
            else next.delete(PARAM_TYP);
            updateParams(next);
        },
        [searchParams, updateParams]
    );

    const availableTypeLabels = useMemo(() => {
        const keys = new Set<string>();
        for (const d of datasets) {
            const tVal = (d.typCode ?? '').trim();
            if (tVal) keys.add(getTypKey(tVal));
        }
        const order: Record<string, number> = {
            cities: 0,
            regions: 1,
            national: 2,
        };
        return Array.from(keys)
            .sort((a, b) =>(order[a] ?? 99) - (order[b] ?? 99))
            .map(key => ({
                key,
                label: t(`types.${key}`)
            }));
    }, [datasets, t]);

    return (
        <div className="dataset-grid-wrap">
            <div className="dataset-grid-filters">
                <div className="dataset-grid-typ-buttons" role="group" aria-label="Typ obce">
                    <button
                        type="button"
                        className={`dataset-grid-typ-btn ${typFilter === '' ? 'dataset-grid-typ-btn--active' : ''}`}
                        onClick={() => setTypFilter('')}
                    >
                        {t('filterAll')}
                    </button>
                    {availableTypeLabels.map((type) => (
                        <button
                            key={type.key}
                            type="button"
                            className={`dataset-grid-typ-btn ${typFilter === type.key ? 'dataset-grid-typ-btn--active' : ''}`}
                            onClick={() => setTypFilter(type.key)}
                        >
                            {type.label}
                        </button>
                    ))}
                </div>
                <InputText
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t('searchPlaceholder')}
                    className="dataset-grid-search"
                />
                <Dropdown
                    value={sort}
                    options={SORT_OPTIONS}
                    onChange={(e) => setSort(e.value)}
                    placeholder={t('sortPlaceholder')}
                    className="dataset-grid-sort"
                />
            </div>
            <div
                className={`dataset-grid ${isPending ? 'dataset-grid--pending' : ''}`}
                aria-busy={isPending}
            >
                {(filteredResult.length > 0 ? filteredResult : datasets).map((item) => (
                    <DatasetCard key={item.id} item={item} />
                ))}
            </div>
        </div>
    );
};

export default DatasetGrid;
